/**
 * DealNexa PI API Service
 * Centralized client for all Portfolio Intelligence API endpoints.
 */

import api from './api';

export const piApi = {
  // ── Dashboard ──────────────────────────────────────────────
  getDashboardStats:    ()           => api.get('/api/pi/dashboard/stats'),
  getPortfolioSummary:  ()           => api.get('/api/pi/dashboard/summary'),

  // ── Portcos ────────────────────────────────────────────────
  getPortcos:           ()           => api.get('/api/pi/portcos'),
  getPortco:            (id)         => api.get(`/api/pi/portcos/${id}`),
  updatePortco:         (id, data)   => api.put(`/api/pi/portcos/${id}`, data),
  getPortcoStats:       (id)         => api.get(`/api/pi/portcos/${id}/stats`),

  // ── DD Context ─────────────────────────────────────────────
  getDDContext:         (portcoId)   => api.get(`/api/pi/portcos/${portcoId}/dd-context`),

  // ── VCP Initiatives ────────────────────────────────────────
  getInitiatives:       (portcoId)           => api.get(`/api/pi/portcos/${portcoId}/initiatives`),
  createInitiative:     (portcoId, data)     => api.post(`/api/pi/portcos/${portcoId}/initiatives`, data),
  updateInitiative:     (portcoId, id, data) => api.put(`/api/pi/portcos/${portcoId}/initiatives/${id}`, data),
  deleteInitiative:     (portcoId, id)       => api.delete(`/api/pi/portcos/${portcoId}/initiatives/${id}`),
  draftVCP:             (portcoId)           => api.post(`/api/pi/portcos/${portcoId}/initiatives/ai-draft`),

  // ── EBITDA Bridge ──────────────────────────────────────────
  getBridgeItems:       (portcoId)           => api.get(`/api/pi/portcos/${portcoId}/bridge`),
  createBridgeItem:     (portcoId, data)     => api.post(`/api/pi/portcos/${portcoId}/bridge`, data),
  updateBridgeItem:     (portcoId, id, data) => api.put(`/api/pi/portcos/${portcoId}/bridge/${id}`, data),
  deleteBridgeItem:     (portcoId, id)       => api.delete(`/api/pi/portcos/${portcoId}/bridge/${id}`),
  analyzeEBITDAVariance:(portcoId)           => api.post(`/api/pi/portcos/${portcoId}/bridge/analyze`),

  // ── Synergies ──────────────────────────────────────────────
  getSynergies:         (portcoId)           => api.get(`/api/pi/portcos/${portcoId}/synergies`),
  getSynergyContext:    (portcoId)           => api.get(`/api/pi/portcos/${portcoId}/synergies/context`),
  createSynergy:        (portcoId, data)     => api.post(`/api/pi/portcos/${portcoId}/synergies`, data),
  updateSynergy:        (portcoId, id, data) => api.put(`/api/pi/portcos/${portcoId}/synergies/${id}`, data),
  deleteSynergy:        (portcoId, id)       => api.delete(`/api/pi/portcos/${portcoId}/synergies/${id}`),

  // ── Portco Financials ─────────────────────────────────────
  getFinancials:        (portcoId)           => api.get(`/api/pi/portcos/${portcoId}/financials`),
  saveFinancials:       (portcoId, data)     => api.post(`/api/pi/portcos/${portcoId}/financials`, data),

  // ── Add-Ons (Linked Portcos) ────────────────────────────────
  getLinkedPortcos:     (portcoId)           => api.get(`/api/pi/portcos/${portcoId}/addons`),
  linkPortco:           (portcoId, data)     => api.post(`/api/pi/portcos/${portcoId}/addons/link`, data),
  createAndLinkPortco:  (portcoId, data)     => api.post(`/api/pi/portcos/${portcoId}/addons/create-and-link`, data),

  // ── Returns ────────────────────────────────────────────────
  getReturnScenarios:   (portcoId)                   => api.get(`/api/pi/portcos/${portcoId}/returns`),
  saveReturnScenario:   (portcoId, scenario, data)   => api.put(`/api/pi/portcos/${portcoId}/returns/${scenario}`, data),

  // ── Exit Readiness ─────────────────────────────────────────
  getExitReadinessItems:(portcoId)           => api.get(`/api/pi/portcos/${portcoId}/exit`),
  createExitItem:       (portcoId, data)     => api.post(`/api/pi/portcos/${portcoId}/exit`, data),
  updateExitItem:       (portcoId, id, data) => api.put(`/api/pi/portcos/${portcoId}/exit/${id}`, data),
  deleteExitItem:       (portcoId, id)       => api.delete(`/api/pi/portcos/${portcoId}/exit/${id}`),
  generateExitChecklist:(portcoId)           => api.post(`/api/pi/portcos/${portcoId}/exit/checklist`),

  // ── Talent ─────────────────────────────────────────────────
  getTalentProfiles:    (portcoId)           => api.get(`/api/pi/portcos/${portcoId}/talent`),
  createTalentProfile:  (portcoId, data)     => api.post(`/api/pi/portcos/${portcoId}/talent`, data),
  updateTalentProfile:  (portcoId, id, data) => api.put(`/api/pi/portcos/${portcoId}/talent/${id}`, data),
  deleteTalentProfile:  (portcoId, id)       => api.delete(`/api/pi/portcos/${portcoId}/talent/${id}`),

  // ── Cash & Tax ─────────────────────────────────────────────
  getCashData:          (portcoId)           => api.get(`/api/pi/portcos/${portcoId}/cash`),
  saveCashData:         (portcoId, data)     => api.put(`/api/pi/portcos/${portcoId}/cash`, data),
  getDebtSchedule:      (portcoId)           => api.get(`/api/pi/portcos/${portcoId}/cash/debt`),
  addDebtRow:           (portcoId, data)     => api.post(`/api/pi/portcos/${portcoId}/cash/debt`, data),
  saveTaxFlags:         (portcoId, flags)    => api.put(`/api/pi/portcos/${portcoId}/cash/tax-flags`, { flags }),
  analyzeCashPosition:  (portcoId)           => api.post(`/api/pi/portcos/${portcoId}/cash/analyze`),

  // ── LP Reporting ───────────────────────────────────────────
  getLPReportsForPortco:(portcoId)           => api.get(`/api/pi/portcos/${portcoId}/lp-reports`),
  getLPReports:         ()                   => api.get('/api/pi/lp-reports'),
  generateLPReport:     (portcoId, period)   => api.post(`/api/pi/portcos/${portcoId}/lp-reports`, { period }),
  updateLPReport:       (portcoId, id, data) => api.put(`/api/pi/portcos/${portcoId}/lp-reports/${id}`, data),
  approveLPReport:      (portcoId, id)       => api.post(`/api/pi/portcos/${portcoId}/lp-reports/${id}/approve`),
  sendLPReport:         (portcoId, id)       => api.post(`/api/pi/portcos/${portcoId}/lp-reports/${id}/send`),

  // ── Import Wizard ──────────────────────────────────────────
  parseImportFile:      (type, file) => {
    const fd = new FormData();
    fd.append('file', file);
    fd.append('type', type);
    return api.post('/api/pi/import/parse', fd, { headers: { 'Content-Type': 'multipart/form-data' } });
  },
  previewImport:        (type, columnMap, preview) => api.post('/api/pi/import/preview', { type, columnMap, preview }),
  confirmImport:        (type, records)            => api.post('/api/pi/import/confirm', { type, records }),
  downloadTemplate:     (type) => { window.location.href = `/api/pi/import/template/${type}`; },
};

export default piApi;
